import React, { useRef, useState, useEffect } from "react";
import { Play, Pause, ShoppingBag, ArrowRight, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { usePlayer } from "@/context/PlayerContext";
import BuyDialog from "@/components/BuyDialog";
import { isSaved, toggleSaved } from "@/lib/saved";

export default function SongCard({ song, index = 0 }) {
  const { current, playing, play, toggle } = usePlayer();
  const videoRef = useRef(null);
  const [videoOn, setVideoOn] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [saved, setSaved] = useState(isSaved(song.id));
  useEffect(() => {
    const h = () => setSaved(isSaved(song.id));
    window.addEventListener("sanctum-saved-change", h);
    return () => window.removeEventListener("sanctum-saved-change", h);
  }, [song.id]);
  const isCurrent = current && current.id === song.id;
  const hasVideo = !!song.video_url;

  const onPreview = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (hasVideo) {
      // toggle inline 30s video preview
      if (!videoOn) {
        setVideoOn(true);
        setTimeout(() => {
          const v = videoRef.current;
          if (!v) return;
          v.currentTime = 0;
          v.play().catch(() => {});
          const stopAt = () => { if (v.currentTime >= 30) { v.pause(); v.removeEventListener("timeupdate", stopAt); } };
          v.addEventListener("timeupdate", stopAt);
        }, 0);
      } else {
        const v = videoRef.current;
        if (v) { if (v.paused) v.play().catch(()=>{}); else v.pause(); }
      }
    } else {
      if (isCurrent) toggle(); else play(song, "preview");
    }
  };

  const primaryFormat = hasVideo ? "mp4" : "mp3";
  const primaryPrice = hasVideo ? song.price_mp4 : song.price_mp3;

  return (
    <div
      className="group fade-up bg-white rounded-2xl overflow-hidden border border-[--sanctum-border] shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.07)] transition-shadow"
      style={{ animationDelay: `${Math.min(index * 50, 400)}ms` }}
      data-testid={`song-card-${song.id}`}
    >
      <div className="relative aspect-video overflow-hidden bg-neutral-900">
        {videoOn && hasVideo ? (
          <video ref={videoRef} src={song.video_url} poster={song.cover_url} playsInline className="w-full h-full object-cover" controls={false} />
        ) : (
          <img src={song.cover_url} alt={song.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <button
          onClick={onPreview}
          data-testid={`preview-btn-${song.id}`}
          className="absolute inset-0 flex items-center justify-center"
          aria-label="Play 30-second preview"
        >
          <span className="w-16 h-16 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
            {(isCurrent && playing) ? <Pause size={22} strokeWidth={1.5} /> : <Play size={22} strokeWidth={1.5} className="ml-1" />}
          </span>
        </button>
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold tabular-nums">
          ${primaryPrice.toFixed(2)}
        </div>
        {song.featured && (
          <div className="absolute top-3 left-3 bg-[--sanctum-accent] text-neutral-900 px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase font-medium">
            Featured
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleSaved(song.id); }}
          data-testid={`save-btn-${song.id}`}
          aria-label={saved ? "Unsave" : "Save for later"}
          className={`absolute bottom-3 right-3 w-9 h-9 rounded-full backdrop-blur flex items-center justify-center transition-colors ${
            saved ? "bg-[--sanctum-accent] text-neutral-900" : "bg-white/85 text-neutral-700 hover:bg-white"
          }`}
        >
          <Heart size={14} strokeWidth={1.75} fill={saved ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="p-5 space-y-3">
        <div>
          <h3 className="font-display text-2xl leading-tight text-neutral-900">{song.title}</h3>
          <p className="text-xs text-neutral-500 tracking-wide mt-1 uppercase">{song.genre} · {song.artist}</p>
        </div>
        {song.description && (
          <p className="text-sm text-neutral-600 leading-relaxed line-clamp-3">{song.description}</p>
        )}
        <div className="flex items-center justify-between pt-2">
          <Link
            to={`/songs/${song.id}`}
            data-testid={`preview-link-${song.id}`}
            className="text-sm text-neutral-900 hover:text-neutral-600 inline-flex items-center gap-1 font-medium"
          >
            Free preview <ArrowRight size={14} strokeWidth={1.75} />
          </Link>
          <button
            onClick={() => setBuyOpen(true)}
            data-testid={`buy-btn-${song.id}`}
            className="btn-pill btn-primary !py-2 !px-4 text-xs"
          >
            <ShoppingBag size={12} strokeWidth={1.5} /> Buy · ${primaryPrice.toFixed(2)}
          </button>
        </div>
      </div>
      <BuyDialog open={buyOpen} onOpenChange={setBuyOpen} song={song} format={primaryFormat} />
    </div>
  );
}
